import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, Box, Collapse } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorIcon from '@mui/icons-material/Error';
import StorageIcon from '@mui/icons-material/Storage';
import { send_request } from '../../../../../utils/Request';
import { hexToPastel } from '../../../../../utils/HexToPastelUtil';
import { CanvasContext } from '../../WorkflowCanvas';
import useZoomCollapse from '../../../../../hooks/useZoomCollapse';
import ComponentSettingsToolbar from './ComponentNodes/ComponentNodeUtil/ComponentSettingsToolbar';

const COL_TYPE_LABELS = {
  VARCHAR2: 'Text',
  DATETIME: 'Date',
  NUMBER: 'Number',
  BOOLEAN: 'Bool',
  CLOB: 'Long Text',
};

const MAX_VISIBLE_COLUMNS = 12;

// ─── Styled components matching InlineNodeView ──────────────────────

const NodeCard = styled('div')(({ theme }) => ({
  width: 520,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  position: 'relative',
}));

const NodeHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'bgcolor' && prop !== 'collapsed',
})(({ bgcolor, collapsed }) => ({
  padding: collapsed ? '14px 18px' : '8px 12px',
  backgroundColor: bgcolor,
  borderRadius: collapsed ? 8 : '8px 8px 0 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  transition: 'padding 0.2s ease, border-radius 0.2s ease',
}));

const ColumnArea = styled('div')(({ theme }) => ({
  padding: '8px 16px 12px',
}));

const ColRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 8px',
  borderRadius: 4,
  backgroundColor: theme.palette.action.hover,
}));

const ColName = styled(Typography)({
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
});

const ColType = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 500,
  color: theme.palette.text.disabled,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  flexShrink: 0,
}));

const MoreText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.disabled,
  textAlign: 'center',
  padding: '4px 8px 0',
}));

const ColCount = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.disabled,
  padding: '0 8px 4px',
}));

const HiddenHandle = { display: 'none' };

const toolbarClasses = {
  nodeInfoBackground: '',
  settings: '',
  centerGrid: '',
  actionButton: '',
  actionText: '',
};

const DBNode = ({ id, data, selected }) => {
  const theme = useTheme();
  const collapsed = useZoomCollapse();
  const [columns, setColumns] = useState([]);
  const [tableName, setTableName] = useState(data.label || 'Table');
  const [isHovered, setIsHovered] = useState(false);

  const { edit } = useContext(CanvasContext);
  const [, setEditComponent] = edit || [];

  const headerBg = useMemo(
    () => hexToPastel(data.baseColor || '#1C1B1F', theme.palette.mode),
    [data.baseColor, theme.palette.mode]
  );

  useEffect(() => {
    if (data.label === 'Database' || data.label === 'Table') {
      send_request(`database/get_database_name/${id}`, null, null, 'GET')
        .then((res) => {
          if (res?.data) {
            setTableName(res.data);
            data.label = res.data;
          }
        })
        .catch(() => {});
    } else if (data.label) {
      setTableName(data.label);
    }

    send_request(`database/get_columns/${id}`, null, null, 'GET')
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) {
          const sorted = [...res.data].sort(
            (a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0)
          );
          setColumns(sorted);
        }
      })
      .catch(() => {});
  }, [id]);

  const openInNewTab = useCallback(() => {
    if (setEditComponent) setEditComponent(null);
    window.open(`${data.newTabUrl}/${id}`, '_blank');
  }, [data.newTabUrl, id, setEditComponent]);

  const actions = [
    {
      title: 'Open table in a new tab',
      icon: <OpenInNewIcon />,
      onClick: openInNewTab,
    },
  ];

  const visibleCols = columns.slice(0, MAX_VISIBLE_COLUMNS);
  const remaining = columns.length - MAX_VISIBLE_COLUMNS;

  return (
    <div
      draggable={false}
      style={{ width: 520, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Handle type="target" position={Position.Top} style={HiddenHandle} />
      <Handle type="source" position={Position.Top} style={HiddenHandle} />

      {data.error && (
        <ErrorIcon sx={{ height: 16, width: 16, color: 'error.main', mb: 0.5 }} />
      )}

      <NodeCard>
        <NodeHeader bgcolor={headerBg} collapsed={collapsed}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: collapsed ? '10px' : '6px' }}>
            {data.logo ? (
              <img
                src={data.logo}
                alt=""
                width={collapsed ? 32 : 20}
                height={collapsed ? 32 : 20}
                style={{ transition: 'width 0.2s ease, height 0.2s ease' }}
              />
            ) : (
              <StorageIcon
                sx={{
                  width: collapsed ? 32 : 20,
                  height: collapsed ? 32 : 20,
                  color: 'text.secondary',
                  transition: 'width 0.2s ease, height 0.2s ease',
                }}
              />
            )}
            <Typography
              sx={{
                fontSize: collapsed ? 22 : 13,
                fontWeight: 600,
                color: 'text.primary',
                transition: 'font-size 0.2s ease',
              }}
            >
              {tableName}
            </Typography>
          </Box>
        </NodeHeader>

        <Collapse in={!collapsed} timeout={200}>
          <ColumnArea>
            <ColCount>
              {columns.length} {columns.length === 1 ? 'column' : 'columns'}
            </ColCount>

            {visibleCols.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visibleCols.map((col) => (
                  <ColRow key={col.columnId || col.name}>
                    <ColName>{col.name || col.columnName}</ColName>
                    <ColType>
                      {COL_TYPE_LABELS[col.type] || col.type || 'Text'}
                    </ColType>
                  </ColRow>
                ))}
                {remaining > 0 && (
                  <MoreText>
                    +{remaining} more column{remaining > 1 ? 's' : ''}
                  </MoreText>
                )}
              </div>
            )}
          </ColumnArea>
        </Collapse>
      </NodeCard>

      <ComponentSettingsToolbar
        selected={selected || isHovered}
        classes={toolbarClasses}
        data={data}
        actions={actions}
      />
    </div>
  );
};

export default React.memo(DBNode);
